import { StatusName } from "../../../services/api/type";
import { GLBService } from "../../../services/GLBService/GLBService";
import PlayCanvasService from "../../../services/PlayCanvasService/PlayCanvasService";
import { getDataProductForGetGlbById } from "../../reducers/ui/selectors/glbSelectors";
import {
  getProductByIdAndPath,
  getCurrentCategoryFolder,
  getProductFromCache,
} from "../../reducers/ui/selectors/objectSelectors";
import { updateProcessGLBData } from "../../reducers/ui/uiReducer";
import { ISelectItemObjectData } from "../../reducers/ui/uiTypes";
import { AppStore } from "../../store";
import { changeShowModal } from "../../reducers/app/appReducer";

export const loadGLBByProductId =
  (productId: string, selectData: ISelectItemObjectData, replace?: boolean) =>
  async (store: AppStore): Promise<Partial<ISelectItemObjectData> | null> => {
    const state = store.getState();

    // First try to get product from currentCategoryFolder (has fetched products)
    const currentCategoryFolder = getCurrentCategoryFolder(state);
    const projectInFolder = currentCategoryFolder?.projects?.find(
      p => p.id === selectData.projectId
    );
    const productFromFolder = projectInFolder?.availableProducts?.find(
      p => p.id === selectData.realId
    );

    // Next try the cache by project
    const productFromCache = getProductFromCache(
      selectData.projectId,
      selectData.realId
    )(state);

    // Fallback to path-based lookup
    const productFromPath = getProductByIdAndPath(
      selectData.realId,
      selectData.pathObjectIds,
      selectData.projectId
    )(state);

    const product = productFromFolder || productFromCache || productFromPath;

    if (!product) {
      console.error("loadGLBByProductId - Product not found!", selectData);
      return null;
    }

    const dataForGLB = getDataProductForGetGlbById(
      productId,
      selectData
    )(state);

    try {
      store.dispatch(
        changeShowModal({ nameModal: "glbLoader", stateModal: true })
      );

      const data = await new GLBService().getGLBData(dataForGLB);

      store.dispatch(
        changeShowModal({ nameModal: "glbLoader", stateModal: false })
      );

      if (data.status === StatusName.SUCCESS && data.url) {
        await loadAssetById(productId, data.url, replace);

        const transformData =
          await PlayCanvasService.getTransformDataAssetById(productId);

        return {
          glbData: {
            url: data.url,
            transform: transformData,
          },
        };
      }

      if (data.status !== StatusName.SUCCESS && data.hash) {
        store.dispatch(
          updateProcessGLBData({
            id: productId,
            glbHash: data.hash,
            selectData,
            prevProductId: null,
          })
        );
      }

      return null;
    } catch (error) {
      console.error("Error loading GLB:", error);

      store.dispatch(
        changeShowModal({ nameModal: "glbLoader", stateModal: false })
      );
      return null;
    }
  };

export const replaceGLBByProductId =
  (
    prevProductId: string,
    newProductId: string,
    selectData: ISelectItemObjectData
  ) =>
  async (store: AppStore): Promise<Partial<ISelectItemObjectData> | null> => {
    const state = store.getState();

    const currentCategoryFolder = getCurrentCategoryFolder(state);
    const projectInFolder = currentCategoryFolder?.projects?.find(
      p => p.id === selectData.projectId
    );
    const productFromFolder = projectInFolder?.availableProducts?.find(
      p => p.id === selectData.realId
    );

    const productFromCache = getProductFromCache(
      selectData.projectId,
      selectData.realId
    )(state);

    const productFromPath = getProductByIdAndPath(
      selectData.realId,
      selectData.pathObjectIds,
      selectData.projectId
    )(state);

    const product = productFromFolder || productFromCache || productFromPath;

    if (!product) {
      console.error("replaceGLBByProductId - Product not found!", selectData);
      return null;
    }

    const dataForGLB = getDataProductForGetGlbById(
      newProductId,
      selectData
    )(state);

    try {
      store.dispatch(
        changeShowModal({ nameModal: "glbLoader", stateModal: true })
      );

      const data = await new GLBService().getGLBData(dataForGLB);

      store.dispatch(
        changeShowModal({ nameModal: "glbLoader", stateModal: false })
      );

      if (data.status === StatusName.SUCCESS && data.url) {
        const prevTransformData =
          await PlayCanvasService.getTransformDataAssetById(prevProductId);
        await PlayCanvasService.removeAssetById(prevProductId);
        await PlayCanvasService.loadAssetWithParams(
          newProductId,
          data.url,
          prevTransformData
        );

        return {
          glbData: {
            url: data.url,
            transform: prevTransformData,
          },
        };
      }

      if (data.status !== StatusName.SUCCESS && data.hash) {
        store.dispatch(
          updateProcessGLBData({
            id: newProductId,
            glbHash: data.hash,
            selectData,
            prevProductId: prevProductId,
          })
        );
      }

      return null;
    } catch (error) {
      console.error("Error replacing GLB:", error);

      store.dispatch(
        changeShowModal({ nameModal: "glbLoader", stateModal: false })
      );
      return null;
    }
  };

function loadAssetById(
  id: number | string,
  url: string,
  replace: boolean = false
): Promise<any> {
  if (replace) {
    return PlayCanvasService.replaceAssetById(id, url);
  }
  return PlayCanvasService.loadAssetById(id, url);
}
